import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/react-hooks";
import { GetSubscriptionDetails } from "./queries";
import { updateSubscriptionPlan } from "./mutations";
import { toPascalCase } from "../../../helpers/utils";

export const useBillingInfoDetails = () => {
  const [changePlan, setChangePlan] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isValid, setIsValid] = useState({ state: true, plan: {} });
  const [error, setError] = useState();
  const [subscriptionPlanId, setSubscriptionPlanId] = useState();
  const [loading, setLoading] = useState(false);

  const { data, loading: queryLoading } = useQuery(GetSubscriptionDetails);
  const [updateSubscription] = useMutation(updateSubscriptionPlan, {
    refetchQueries: [{ query: GetSubscriptionDetails }],
    awaitRefetchQueries: true
  });

  const account = data && data.accounts && data.accounts[0];
  const balance = account && account.balance || 0;
  const { fixedFee, variableFee } = account || {};
  const subscription = (account && account.subscription) || {};

  const subscriptionPlans = (account && account.subscriptionPlans) || [];
  const customerInvoices = (account && account.invoices) || [];

  const currentSubscriptionPlan =
    (subscription && subscription.subscriptionPlan) || {};

  const paymentMethod =
    (account &&
      account.paymentMethods.filter((method) => method.isDefault)[0]) ||
    {};

  const subscriptionList =
    (subscriptionPlans &&
      subscriptionPlans.map(({ id, nickname, amount }) => {
        return {
          value: id,
          label: `${toPascalCase(nickname).replace(" ", "-")} $${amount}/month`
        };
      })) ||
    [];

  const onConfirmSubscription = async () => {
    if (subscriptionPlanId !== currentSubscriptionPlan.id) {
      setLoading(true);
      const {
        data: { updateSubscriptionPlan: subscriptionStatus }
      } = await updateSubscription({
        variables: {
          subscriptionPlanId
        }
      });

      setLoading(false);
      setIsAlertOpen(false);

      if (subscriptionStatus === "NotUpdated") {
        setError(getError());
        return;
      }
      setChangePlan(false);
    }
  };

  const onChangeSubscription = (id) => {
    setError(false);
    const subscriptionPlan = subscriptionPlans.filter((x) => x.id == id)[0];
    const {
      videoLimit,
      opportunityLimit,
      transactionVolumeLimit,
      stripePlanId
    } = subscriptionPlan;

    const {
      videoCount,
      opportunityCount,
      transactionVolumeAmount
    } = account.usageStats;
    if (
      videoCount <= videoLimit &&
      opportunityCount <= opportunityLimit &&
      transactionVolumeAmount <= transactionVolumeLimit &&
      !!stripePlanId
    ) {
      setIsValid({ state: true, plan: subscriptionPlan });
      setSubscriptionPlanId(id);
    } else {
      setIsValid({ state: false, plan: subscriptionPlan });
    }
    setIsAlertOpen(true);
  };

  const alertMessage = isValid.state
    ? `Are you sure you want to change the subscription plan to ${toPascalCase(
        isValid.plan.nickname
      )}`
    : !!isValid.plan.stripePlanId
    ? `Error: Your subscription plan cannot be downgraded to ${toPascalCase(
        isValid.plan.nickname
      )}`
    : `Error: You cannot subscribe to a partnered plan`;

  return {
    balance,
    subscription,
    currentSubscriptionPlan,
    paymentMethod,
    subscriptionList,
    changePlan,
    setChangePlan,
    isAlertOpen,
    setIsAlertOpen,
    setSubscriptionPlanId,
    customerInvoices,
    fixedFee: parseInt(fixedFee * 100),
    variableFee: parseFloat(variableFee * 100).toFixed(2),
    error,
    isValid,
    pageIsLoading: queryLoading,
    loading,
    alertMessage,
    onConfirmSubscription,
    onChangeSubscription
  };
};

const getError = () => {
  return {
    message: (
      <span>
        Something went wrong! Try again or contact{" "}
        {
          <a
            href="https://help.videosquirrel.app/"
            target="_blank"
            rel="noopener noreferrer"
          >
            support
          </a>
        }{" "}
        if the problem persists.
      </span>
    )
  };
};
